import { useState, useEffect, useRef } from "react";
import { Filter, X } from "lucide-react";

/**
 * Filter panel for the Grant Financial Details table.
 * Renders a "Filters" button. Clicking opens a small popover with inline
 * inputs for: Grant ID, Grantee, Themes (multi-checkbox), and numeric ranges
 * for Sanctioned / Disbursed / Disb% / Payments.
 *
 * Filters STACK on top of the page's global filters — they narrow the result
 * set further server-side via the same /financial/grants-table endpoint.
 *
 * Props:
 *   filters         {object}   current filter state
 *   onChange(next)  (fn)       receives the new filters object
 *   themeOptions    string[]   available themes for the multi-checkbox
 *   filteredCount   number     row count after applying these filters (for the badge)
 *   totalCount      number     unfiltered count (denominator)
 */
export default function GrantsTableFilters({
  filters,
  onChange,
  themeOptions = [],
  filteredCount = 0,
  totalCount = 0,
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(filters);
  const popoverRef = useRef(null);

  // Re-sync draft when external filters change (e.g. Clear all from outside)
  useEffect(() => { setDraft(filters); }, [filters]);

  // Close on outside-click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const activeCount = countActive(filters);
  const hasActive = activeCount > 0;

  const apply = () => { onChange(draft); setOpen(false); };
  const clearAll = () => {
    const empty = emptyFilters();
    setDraft(empty);
    onChange(empty);
  };

  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const toggleTheme = (t) => {
    const next = new Set(draft.themes || []);
    next.has(t) ? next.delete(t) : next.add(t);
    set("themes", Array.from(next));
  };

  return (
    <div className="relative inline-flex items-center gap-2" ref={popoverRef}>
      {hasActive && (
        <span
          data-testid="grants-filter-badge"
          className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 border border-red-100 text-[#B71C1C] text-[10px] font-medium rounded-full"
        >
          {filteredCount.toLocaleString()} of {totalCount.toLocaleString()} grants
          <button
            type="button"
            onClick={clearAll}
            data-testid="grants-filter-clear-all"
            className="ml-1 hover:text-red-700"
            title="Clear all filters"
          >
            <X size={11} />
          </button>
        </span>
      )}

      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="grants-filter-toggle"
        className={`inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded border font-medium transition-colors ${
          hasActive
            ? "bg-[#B71C1C] text-white border-[#B71C1C]"
            : "bg-white text-slate-700 border-slate-200 hover:border-[#B71C1C]"
        }`}
      >
        <Filter size={13} />
        Filters
        {hasActive && (
          <span className="ml-0.5 px-1 py-px bg-white/25 rounded text-[9px] font-semibold">
            {activeCount}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="grants-filter-panel"
          className="absolute right-0 top-full mt-1 z-30 w-[440px] bg-white border border-slate-200 rounded-xl shadow-2xl p-4 animate-fade-in"
        >
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-semibold text-slate-900">Filter grants</h4>
            <button
              type="button"
              onClick={clearAll}
              data-testid="grants-filter-clear-inside"
              className="text-[10px] text-slate-500 hover:text-[#B71C1C] underline decoration-dotted"
            >
              Clear all
            </button>
          </div>

          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <TextInput label="Grant ID" value={draft.grant_id_q} onChange={v => set("grant_id_q", v)} placeholder="e.g. 12345" testid="filter-grant-id" />
            <TextInput label="Grantee or project title" value={draft.grantee_q} onChange={v => set("grantee_q", v)} placeholder="search by name…" testid="filter-grantee" />

            <div>
              <label className="text-[10px] font-medium text-slate-600 uppercase tracking-wide">Theme</label>
              <div className="mt-1 grid grid-cols-2 gap-1 max-h-32 overflow-y-auto border border-slate-200 rounded p-2">
                {themeOptions.length === 0 && (
                  <span className="text-[10px] text-slate-400 col-span-2">No themes available</span>
                )}
                {themeOptions.map(t => (
                  <label key={t} className="flex items-center gap-1.5 text-[11px] text-slate-700 cursor-pointer hover:text-[#B71C1C]">
                    <input
                      type="checkbox"
                      checked={(draft.themes || []).includes(t)}
                      onChange={() => toggleTheme(t)}
                      className="accent-[#B71C1C]"
                      data-testid={`filter-theme-${t}`}
                    />
                    <span className="truncate" title={t}>{t}</span>
                  </label>
                ))}
              </div>
            </div>

            <RangeInput label="Sanctioned (₹ Cr)"
              minVal={draft.min_sanctioned_cr} maxVal={draft.max_sanctioned_cr}
              onMin={v => set("min_sanctioned_cr", v)} onMax={v => set("max_sanctioned_cr", v)}
              step="0.01" testid="filter-sanctioned" />
            <RangeInput label="Disbursed (₹ Cr)"
              minVal={draft.min_disbursed_cr} maxVal={draft.max_disbursed_cr}
              onMin={v => set("min_disbursed_cr", v)} onMax={v => set("max_disbursed_cr", v)}
              step="0.01" testid="filter-disbursed" />
            <RangeInput label="Disbursement % (0–100)"
              minVal={draft.min_disb_pct} maxVal={draft.max_disb_pct}
              onMin={v => set("min_disb_pct", v)} onMax={v => set("max_disb_pct", v)}
              step="1" min="0" max="100" testid="filter-disb-pct" />
            <RangeInput label="# of payments"
              minVal={draft.min_payments} maxVal={draft.max_payments}
              onMin={v => set("min_payments", v)} onMax={v => set("max_payments", v)}
              step="1" min="0" testid="filter-payments" />
          </div>

          <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="grants-filter-cancel"
              className="text-[11px] px-3 py-1.5 rounded text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={apply}
              data-testid="grants-filter-apply"
              className="text-[11px] px-3 py-1.5 bg-[#B71C1C] text-white rounded hover:bg-[#8a0e0e] font-medium"
            >
              Apply filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TextInput({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-[10px] font-medium text-slate-600 uppercase tracking-wide">{label}</label>
      <input
        type="text"
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-1 w-full text-xs px-2.5 py-1.5 border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#B71C1C] focus:border-[#B71C1C]"
      />
    </div>
  );
}

function RangeInput({ label, minVal, maxVal, onMin, onMax, step = "1", min, max, testid }) {
  return (
    <div>
      <label className="text-[10px] font-medium text-slate-600 uppercase tracking-wide">{label}</label>
      <div className="mt-1 flex items-center gap-2">
        <input
          type="number"
          value={minVal ?? ""}
          step={step} min={min} max={max}
          placeholder="min"
          onChange={e => onMin(e.target.value === "" ? null : Number(e.target.value))}
          data-testid={`${testid}-min`}
          className="flex-1 text-xs px-2.5 py-1.5 border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#B71C1C] focus:border-[#B71C1C]"
        />
        <span className="text-[10px] text-slate-400">to</span>
        <input
          type="number"
          value={maxVal ?? ""}
          step={step} min={min} max={max}
          placeholder="max"
          onChange={e => onMax(e.target.value === "" ? null : Number(e.target.value))}
          data-testid={`${testid}-max`}
          className="flex-1 text-xs px-2.5 py-1.5 border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#B71C1C] focus:border-[#B71C1C]"
        />
      </div>
    </div>
  );
}

export function emptyFilters() {
  return {
    grant_id_q: "",
    grantee_q: "",
    themes: [],
    min_sanctioned_cr: null, max_sanctioned_cr: null,
    min_disbursed_cr: null,  max_disbursed_cr: null,
    min_disb_pct: null,      max_disb_pct: null,
    min_payments: null,      max_payments: null,
  };
}

function countActive(f) {
  let n = 0;
  if (f.grant_id_q) n++;
  if (f.grantee_q) n++;
  if ((f.themes || []).length > 0) n++;
  if (f.min_sanctioned_cr != null || f.max_sanctioned_cr != null) n++;
  if (f.min_disbursed_cr != null || f.max_disbursed_cr != null) n++;
  if (f.min_disb_pct != null || f.max_disb_pct != null) n++;
  if (f.min_payments != null || f.max_payments != null) n++;
  return n;
}

/** Convert filter state into API query params (only includes non-empty values). */
export function filtersToParams(f) {
  const p = {};
  if (f.grant_id_q) p.grant_id_q = f.grant_id_q;
  if (f.grantee_q) p.grantee_q = f.grantee_q;
  if (f.themes && f.themes.length) p.themes = f.themes.join(",");
  if (f.min_sanctioned_cr != null) p.min_sanctioned_cr = f.min_sanctioned_cr;
  if (f.max_sanctioned_cr != null) p.max_sanctioned_cr = f.max_sanctioned_cr;
  if (f.min_disbursed_cr != null)  p.min_disbursed_cr  = f.min_disbursed_cr;
  if (f.max_disbursed_cr != null)  p.max_disbursed_cr  = f.max_disbursed_cr;
  if (f.min_disb_pct != null)      p.min_disb_pct      = f.min_disb_pct;
  if (f.max_disb_pct != null)      p.max_disb_pct      = f.max_disb_pct;
  if (f.min_payments != null)      p.min_payments      = f.min_payments;
  if (f.max_payments != null)      p.max_payments      = f.max_payments;
  return p;
}
